import { UserAvatarFactory } from "../../UserAvatar/UserAvatarFactory";
import {
    AvatarUserPostAvatar,
    CardHeaderPostHeader,
    GroupSpan,
    PostCardHeaderTitleSection,
    PostOptionsButton,
    TextNonBreakable,
    TypographyClickable,
    TypographyHeaderSubTitle,
    TypographyHeaderSubTitleBox,
    TypographyPostHeaderTitle,
} from "../Post.styles";
import { EllipsisHorizontalIcon } from "@icons/EllipsisHorizontalIcon";
import { Box, IconButton } from "@mui/material";
import { getUserName } from "../../../Shared/UserHelpers";
import { UserTrustBadge } from "../../UserTrustBadge/UserTrustBadge";
import { UserRoleBadgeFactory } from "../../UserRoleBadge/UserRoleBadgeFactory";
import React from "react";
import { AvatarOptions } from "../../UserAvatar/UserAvatar.types";
import userAvatarChecker from "../../../Shared/UserAvatarChecker";
import { isUserCreator, UserType } from "../../../Shared/types";
import { PostDataType, PostGroupType } from "../Post.types";
import dayjs from "dayjs";
import getRelativeDate from "../../RelativeDate/RelativeDate";
import { usePostDetailsModalStore } from "../../Modals/PostDetailsModal/PostDetailsModalStore";
import { CloseIcon } from "@icons/CloseIcon";

export function PostHeaderDefault({
    openPostAvatarModal,
    user,
    onSettingsClickHandler,
    onPostGroupTitleClickHandler,
    group,
    postData,
    showGroupName,
    onLocationClick,
    showClosingButton,
    isHighlighted,
    handleCloseForHeaderComponent,
}: {
    group: PostGroupType | undefined;
    isHighlighted: boolean;
    onLocationClick: (e: React.MouseEvent<HTMLElement>) => void;
    onPostGroupTitleClickHandler: (e: React.MouseEvent<HTMLElement>) => void;
    onSettingsClickHandler: (e: React.MouseEvent<HTMLElement>) => void;
    openPostAvatarModal: (e: React.MouseEvent<HTMLElement>) => void;
    postData: PostDataType;
    showClosingButton: boolean;
    showGroupName: boolean;
    user: UserType;
    handleCloseForHeaderComponent?: () => void;
}) {
    const postDate = dayjs(postData.createdAt);
    const relativeDate = getRelativeDate(postDate.toDate());
    const { setIsPostDetailsModalOpen } = usePostDetailsModalStore();
    const avatarOptions: AvatarOptions = {
        uuid: user.uuid,
        avatar: user.avatar,
        gender: user.gender,
        onClick: openPostAvatarModal,
        onError: () => userAvatarChecker(user.avatar, user.uuid),
        component: AvatarUserPostAvatar,
        sx: {
            cursor: "pointer",
        },
    };

    const trustedBadgeOpts = {
        ...user.trust,
    };

    return (
        <CardHeaderPostHeader
            avatar={UserAvatarFactory(avatarOptions)}
            action={
                <Box style={{ display: "flex", alignItems: "center" }}>
                    <PostOptionsButton
                        aria-label="settings"
                        onClick={onSettingsClickHandler}
                        disableFocusRipple
                    >
                        <EllipsisHorizontalIcon />
                    </PostOptionsButton>
                    {showClosingButton && (
                        <IconButton
                            aria-label="close"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCloseForHeaderComponent
                                    ? handleCloseForHeaderComponent()
                                    : setIsPostDetailsModalOpen(false);
                            }}
                            sx={{
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            }
            title={
                <PostCardHeaderTitleSection>
                    <Box sx={{ display: "flex" }} onClick={openPostAvatarModal}>
                        <TypographyPostHeaderTitle>
                            {getUserName(user?.screenName)}
                        </TypographyPostHeaderTitle>
                        <UserTrustBadge {...trustedBadgeOpts} />
                    </Box>

                    <UserRoleBadgeFactory roleBadge={user.role} />
                </PostCardHeaderTitleSection>
            }
            subheader={
                <TypographyHeaderSubTitleBox>
                    {postData.location && (
                        <TypographyHeaderSubTitle>
                            <TextNonBreakable>Ist hier:&nbsp;</TextNonBreakable>
                            <TypographyClickable
                                onClick={onLocationClick}
                                variant="body2"
                            >
                                {postData.location}
                            </TypographyClickable>
                        </TypographyHeaderSubTitle>
                    )}
                    {relativeDate}
                    {relativeDate === "gestern"
                        ? ` ${postDate.format("HH:mm")}`
                        : ""}
                    {showGroupName && (
                        <span onClick={onPostGroupTitleClickHandler}>
                            {group?.name && " | "}
                            <GroupSpan>
                                {group?.name && group?.name}
                                {group?.parentName && ", " + group?.parentName}
                            </GroupSpan>
                        </span>
                    )}
                    {!isHighlighted &&
                        postData.createdAt !== postData.updatedAt &&
                        !isUserCreator(user.role) &&
                        " (editiert)"}
                </TypographyHeaderSubTitleBox>
            }
        />
    );
}
